import { openSignIn } from "@bn-games/auth-client";
import { t } from "@bn-games/i18n-core";
import { el } from "./dom";
import { openModal, type ModalAction, type ModalHandle } from "./Modal";

/**
 * Mirrors `ResumeFailureCause` from `resumeRoom` in `@bn-games/room-client`
 * (M6 resume-parity plan, T2). Duplicated as a local string-literal union
 * rather than imported — `ui-shell` takes no dependency on `room-client`,
 * and these four values are the shared wire-level contract both sides
 * already agree on, not an implementation detail either package owns.
 */
export type ResumeFailureCause = "SEAT_TAKEN" | "ROOM_GONE" | "ROOM_FULL" | "UNREACHABLE";

export interface ResumeFailureModalOptions {
  readonly cause: ResumeFailureCause;
  /** Whether the *current* browser session is signed in — not whether the seat itself is. */
  readonly signedIn: boolean;
  readonly onRetry?: () => void;
  /** Fired once the sign-in popup resolves, success or not — a hook for "try again" wiring. */
  readonly onSignIn?: () => void;
  /** Per-app recovery actions (e.g. View saved position / Discard), rendered after the built-ins. */
  readonly extraActions?: readonly ModalAction[];
}

type ResumeModalKey =
  | "seatTakenTitle"
  | "seatTakenBody"
  | "roomGoneTitle"
  | "roomGoneBody"
  | "roomFullTitle"
  | "roomFullBody"
  | "anonymousTitle"
  | "anonymousBody"
  | "unreachableTitle"
  | "unreachableBody"
  | "signIn"
  | "retry";

interface CauseCopy {
  readonly titleKey: ResumeModalKey;
  readonly bodyKey: ResumeModalKey;
  readonly retryable: boolean;
  readonly signInable: boolean;
}

function resolveCopy(cause: ResumeFailureCause, signedIn: boolean): CauseCopy {
  switch (cause) {
    case "SEAT_TAKEN":
      // Same identity fails identically every retry — no Retry action.
      return { titleKey: "seatTakenTitle", bodyKey: "seatTakenBody", retryable: false, signInable: true };
    case "ROOM_GONE":
      return { titleKey: "roomGoneTitle", bodyKey: "roomGoneBody", retryable: true, signInable: false };
    case "ROOM_FULL":
      // Signed in and still refused: they are genuinely not in this room.
      // Anonymous: this browser has no proof the seat is theirs — the one
      // refusal we cannot avoid (see plan's "The refusal we cannot avoid").
      return signedIn
        ? { titleKey: "roomFullTitle", bodyKey: "roomFullBody", retryable: false, signInable: false }
        : { titleKey: "anonymousTitle", bodyKey: "anonymousBody", retryable: false, signInable: true };
    case "UNREACHABLE":
      return { titleKey: "unreachableTitle", bodyKey: "unreachableBody", retryable: true, signInable: false };
  }
}

// `t()` is typed per-app via each app's i18next.d.ts augmentation to a
// literal union of dictionary key paths — a templated key (`online.
// resumeModal.${key}`) can never satisfy that, so each key is spelled out
// as its own literal call rather than interpolated.
function resumeModalT(key: ResumeModalKey): string {
  switch (key) {
    case "seatTakenTitle":
      return t("online.resumeModal.seatTakenTitle");
    case "seatTakenBody":
      return t("online.resumeModal.seatTakenBody");
    case "roomGoneTitle":
      return t("online.resumeModal.roomGoneTitle");
    case "roomGoneBody":
      return t("online.resumeModal.roomGoneBody");
    case "roomFullTitle":
      return t("online.resumeModal.roomFullTitle");
    case "roomFullBody":
      return t("online.resumeModal.roomFullBody");
    case "anonymousTitle":
      return t("online.resumeModal.anonymousTitle");
    case "anonymousBody":
      return t("online.resumeModal.anonymousBody");
    case "unreachableTitle":
      return t("online.resumeModal.unreachableTitle");
    case "unreachableBody":
      return t("online.resumeModal.unreachableBody");
    case "signIn":
      return t("online.resumeModal.signIn");
    case "retry":
      return t("online.resumeModal.retry");
  }
}

/**
 * Stacked sub-dialog opened from the "Sign in" action — the same
 * signed-out provider-button pattern as `FriendPicker`'s `renderSignedOut`,
 * reused rather than duplicated. `Modal.ts`'s `focusPrimary` scoping to the
 * modal's own card exists precisely so a nested dialog like this one can
 * own its own focus without fighting the outer modal.
 *
 * Signing in cannot reclaim a seat bound anonymously — `resolveSeat` fixes
 * identity at first-seat time and never rebinds it. This is offered as the
 * fix for *next* time, never framed as fixing the current seat.
 */
function openSignInPicker(onDone: () => void): void {
  let picker: ModalHandle;
  const finish = (): void => {
    picker.close();
    onDone();
  };
  const body = el(
    "div",
    { class: "resume-modal__signin" },
    [
      el("button", {
        class: "auth-widget__provider-btn auth-widget__provider-btn--google",
        text: t("auth.google"),
        on: { click: () => { void openSignIn("google").finally(finish); } },
      }),
      el("button", {
        class: "auth-widget__provider-btn auth-widget__provider-btn--github",
        text: t("auth.github"),
        on: { click: () => { void openSignIn("github").finally(finish); } },
      }),
    ],
  );
  picker = openModal({ title: resumeModalT("signIn"), body, actions: [] });
}

function buildActions(
  copy: CauseCopy,
  opts: ResumeFailureModalOptions,
  closeSelf: () => void,
): ModalAction[] {
  const actions: ModalAction[] = [];

  if (copy.retryable && opts.onRetry) {
    const onRetry = opts.onRetry;
    actions.push({
      label: resumeModalT("retry"),
      primary: true,
      onClick: () => {
        closeSelf();
        onRetry();
      },
    });
  }

  if (copy.signInable) {
    actions.push({
      label: resumeModalT("signIn"),
      primary: actions.length === 0,
      onClick: () => {
        openSignInPicker(() => {
          closeSelf();
          opts.onSignIn?.();
        });
      },
    });
  }

  if (opts.extraActions) {
    for (const action of opts.extraActions) {
      actions.push(action);
    }
  }

  return actions;
}

export function openResumeFailureModal(opts: ResumeFailureModalOptions): ModalHandle {
  const copy = resolveCopy(opts.cause, opts.signedIn);
  let modal: ModalHandle;
  const closeSelf = (): void => modal.close();
  modal = openModal({
    title: resumeModalT(copy.titleKey),
    body: resumeModalT(copy.bodyKey),
    actions: buildActions(copy, opts, closeSelf),
    // Non-dismissible: the player must pick an action (Retry / Sign in /
    // an app-supplied recovery) rather than click-outside past the refusal.
    dismissible: false,
  });
  return modal;
}
